import { ImageMediaUrl } from "@common/components/Media/component";
import { pickUserProfilePictureProps, UserProfilePicture } from "@common/components/ProfilePicture/component";
import Badge from "@common/controls/Badge/component";
import { type GridColumn } from "@common/controls/Grid/types";
import GridView from "@common/controls/GridView/component";
import { type GridViewGroupBy, type GridViewGroupingInfo } from "@common/controls/GridView/types";
import Icon from "@common/controls/Icon/component";
import OKModal from "@common/controls/Modal/Modals/OK/component";
import Movement from "@common/controls/Movement/component";
import ResponsiveContainer from "@common/controls/ResponsiveContainer/component";
import { IconType, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { i18nFormat } from "@common/lib/i18n";
import { getCompetitionBadge } from "@common/modules/affiliate/Widget/lib";
import Network from "@common/modules/network/types";
import classNames from "classnames";
import { isUndefined } from "lodash";
import * as React from "react";
import { type ResizePayload, useResizeDetector } from "react-resize-detector";
import { RewardTitle } from "./Components";
import { formatRewardTitle } from "./lib";

import "./style.less";

export type AffiliateLeaderboardProps = {
  competition: Network.Incentives.Incentive;
  currentItem: Network.Incentives.Api.IncentiveLeaderboardItem | null;
  rewards: Network.Incentives.Reward[];

  loadPage: (pageNumber: number, pageSize: number) => Promise<Network.Incentives.IncentiveLeaderboardItem[]>;
};
const AffiliateLeaderboard = (props: AffiliateLeaderboardProps) => (
  <AffiliateLeaderboardCustomized {...props}>
    {(args) => (
      <>
        <div className="heading">
          <div className="title">{args.title}</div>
        </div>
        {args.logo}
        <div className="content">
          <div className="left">{args.grid}</div>

          {!!args.rewards && <div className="right">{args.rewards}</div>}
        </div>
      </>
    )}
  </AffiliateLeaderboardCustomized>
);
export default AffiliateLeaderboard;

export type AffiliateLeaderboardCustomizedChildrenArgs = {
  logo: any;
  title: any;
  grid: any;
  rewards: any;
};
type AffiliateLeaderboardCustomizedProps = AffiliateLeaderboardProps & {
  children: (args: AffiliateLeaderboardCustomizedChildrenArgs) => React.ReactNode;
};
export const AffiliateLeaderboardCustomized = (props: AffiliateLeaderboardCustomizedProps) => {
  const i18n = useI18n();

  const [items, setItems] = React.useState<Network.Incentives.Api.IncentiveLeaderboardItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  const loadPage = React.useCallback(
    async (page: number, size: number) => {
      setIsLoading(true);
      try {
        const data = await props.loadPage(page, size);
        setItems(data);
      } finally {
        setIsLoading(false);
      }
    },
    [props.loadPage]
  );

  React.useEffect(() => {
    loadPage(1, 25);
  }, []);

  const sortedRewards = React.useMemo(() => sort(props.rewards, { key: "sortOrder" }), [props.rewards]);

  const [isCompact, setIsCompact] = React.useState(!sortedRewards.length);
  const [activeRewardIndex, setActiveRewardIndex] = React.useState<number>();

  const grouping = React.useMemo<
    GridViewGroupingInfo<Network.Incentives.Api.IncentiveLeaderboardItem> | undefined
  >(() => {
    if (!items || !sortedRewards.length) return;

    return {
      groupBy: [
        ...sortedRewards.map<GridViewGroupBy>((x, i) => ({
          id: x.incentiveRewardId,
          label: (
            <div className="group_content">
              <RewardTitle index={i} reward={x} />

              {isCompact && (
                <a href="#" onClick={(e) => (e.preventDefault(), setActiveRewardIndex(i))}>
                  {i18n.learnMore}
                </a>
              )}
            </div>
          ),
          childIds: items.filter((y) => y.incentiveRewardId === x.incentiveRewardId).map((y) => y.userId)
        })),
        {
          id: null,
          label: i18n.otherChallengers,
          childIds: items.filter((y) => y.incentiveRewardId === null).map((y) => y.userId)
        }
      ].filter((x) => x.childIds.length)
    };
  }, [sortedRewards, items, isCompact]);

  const columns = React.useMemo(
    (): GridColumn<Network.Incentives.IncentiveLeaderboardItem>[] => [
      {
        key: "position",
        name: i18n.position,
        dataType: "number",
        data: (x) => (
          <div className="position">
            {x.position}
            {!!x.movement && <Movement value={x.movement} />}
          </div>
        ),
        qa: "Position",
        width: "10%"
      },
      {
        key: "firstName",
        name: i18n.name,
        dataType: "string",
        data: (x) => <UserProfilePicture {...pickUserProfilePictureProps(x)} size={36} />,
        qa: "Name"
      },
      {
        key: "points",
        name: i18n.points,
        dataType: "number",
        qa: "Points",
        align: "right"
      }
    ],
    [i18n]
  );

  const onResized = React.useCallback(
    (args: ResizePayload) => {
      if (!args.width) return;

      const isCompact = args.width < 979;
      setIsCompact(isCompact);
      if (!isCompact && !isUndefined(activeRewardIndex)) setActiveRewardIndex(undefined);
    },
    [activeRewardIndex]
  );

  const { ref } = useResizeDetector({
    handleWidth: !!sortedRewards.length,
    onResize: onResized
  });

  return (
    <div ref={ref} className={classNames("affiliate_leaderboard", { compact: isCompact })}>
      {props.children({
        logo: isCompact && !!props.competition.coverPictureMediaUrl && (
          <img className="top_image" src={props.competition.coverPictureMediaUrl} />
        ),
        title: (
          <>
            <h3>{i18n.leaderboard}</h3>
            {getCompetitionBadge(i18n, props.competition)}
          </>
        ),
        grid: (
          <GridView
            columns={columns}
            idKey="userId"
            grouping={grouping}
            hint={i18nFormat(i18n.affiliateCompetitionEarnPointsInfo, ["NUMBER", props.currentItem?.requiredPoints])}
            isHeadless
            isLoading={isLoading}
            isRowHighlighted={(x) => x.userId === props.currentItem?.userId}
            data={items}
            noDataMode="title"
            noDataText={i18n.leaderboardWillAppearHere}
            pageSize={25}
            preHeader={props.currentItem ? [props.currentItem] : null}
            preHeaderGap={20}
            toolbar={{
              showAdd: false,
              showSearch: false
            }}
            itemMenu={null}
            onPageChanged={loadPage}
          />
        ),
        rewards: !!sortedRewards.length && !isCompact && (
          <div className="rewards_block">
            {props.competition.coverPictureMediaUrl && (
              <ResponsiveContainer aspectRatio={2}>
                <ImageMediaUrl url={props.competition.coverPictureMediaUrl} coverMode="cover" />
              </ResponsiveContainer>
            )}

            {sortedRewards.map((x, i) => (
              <div key={x.incentiveRewardId} className="item">
                <Badge color={SystemColor.Gray}>
                  <Icon icon={IconType.Medal} className="prize" />

                  {i18nFormat(i18n.affiliateCompetitionPrizeInfo, ["NUMBER", i + 1])}
                </Badge>
                <h5 className="name">{x.name}</h5>
                <div className="text" dangerouslySetInnerHTML={{ __html: x.description }} />
              </div>
            ))}
          </div>
        )
      })}

      {!isUndefined(activeRewardIndex) && (
        <OKModal
          button={{
            caption: i18n.close,
            type: "white"
          }}
          isVisible
          title={formatRewardTitle(i18n, sortedRewards[activeRewardIndex], activeRewardIndex)}
          onClose={() => setActiveRewardIndex(undefined)}
          width={450}
        >
          <div dangerouslySetInnerHTML={{ __html: sortedRewards[activeRewardIndex].description }} />
        </OKModal>
      )}
    </div>
  );
};
